import { FC, PointerEvent, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { AppContext } from '../contexts/AppContext';

/** Width of the thumb and its distance from the edges of the scroll area, in px */
const WIDTH = 8;
const MARGIN = 4;
const MIN_HEIGHT = 40;

const Thumb = styled.div<{ $color: string }>`
  position: fixed;
  z-index: 30;
  width: ${WIDTH}px;
  border-radius: ${WIDTH / 2}px;
  background-color: ${p => p.$color};
  touch-action: none;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  /* Hidden while the menu is open (it lies below this thumb), and while a lightbox or the DM object dialog is open (their backdrops are see-through) */
  body.menu-open &, body.lightbox-open &, body.modal-open & {
    display: none;
  }
`;

interface ScrollIndicatorProps {
  /** The element that scrolls vertically */
  target?: HTMLElement | null,
  color?: string
}

interface Geometry {
  top: number,
  left: number,
  height: number,
  /** Pixels scrolled per pixel the thumb is moved */
  ratio: number
}

/**
 * A scrollbar that lies on top of the content, instead of taking up space beside it like a native scrollbar
 * (so full width images reach the edge). Shown when the target can scroll. The thumb can be dragged.
 * Rendered in document.body, since it is positioned relative to the screen.
 * @returns {JSX.Element} Component template
 */
export const ScrollIndicator: FC<ScrollIndicatorProps> = ({ target, color }) => {
  const { theme } = useContext(AppContext);
  const [geometry, setGeometry] = useState<Geometry | null>(null);
  const drag = useRef<{ y: number, scrollTop: number } | null>(null);

  useEffect(() => {
    if (!target) { return; }
    let frame = 0;

    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const { scrollTop, scrollHeight, clientHeight, clientWidth } = target;
        const maxScroll = scrollHeight - clientHeight;
        if (maxScroll <= 1) {
          setGeometry(null);
          return;
        }
        const rect = target.getBoundingClientRect();
        const areaTop = Math.max(rect.top, 0);
        const track = Math.min(rect.top + clientHeight, window.innerHeight) - areaTop - 2 * MARGIN;
        const height = Math.max(track * clientHeight / scrollHeight, MIN_HEIGHT);
        setGeometry({
          top: areaTop + MARGIN + (track - height) * scrollTop / maxScroll,
          left: rect.left + clientWidth - WIDTH - MARGIN,
          height,
          ratio: maxScroll / Math.max(track - height, 1)
        });
      });
    };

    // The content can change size (images loading, more results), so its elements are observed too
    const resizeObserver = new ResizeObserver(update);
    const observeChildren = () => {
      resizeObserver.observe(target);
      Array.from(target.children).forEach(child => resizeObserver.observe(child));
    };
    const mutationObserver = new MutationObserver(() => {
      observeChildren();
      update();
    });

    observeChildren();
    mutationObserver.observe(target, { childList: true });
    target.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    update();

    return () => {
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      target.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [target]);

  if (!target || !geometry) { return null; }

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { y: e.clientY, scrollTop: target.scrollTop };
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (drag.current) { target.scrollTop = drag.current.scrollTop + (e.clientY - drag.current.y) * geometry.ratio; }
  };
  const onPointerUp = () => { drag.current = null; };

  return createPortal(
    <Thumb $color={color || theme?.accentColor || "#888"} style={{ top: geometry.top, left: geometry.left, height: geometry.height }}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} aria-hidden="true"/>,
    document.body
  );
}

/**
 * Scrollbar for views that scroll the whole app (#root), e.g. articles. Only shown when the kiosk has
 * accessibility.showScrollbars
 * @returns {JSX.Element} Component template
 */
export const RootScrollIndicator: FC = () => {
  const { deviceData } = useContext(AppContext);
  if (!deviceData?.accessibility?.showScrollbars) { return null; }
  return <ScrollIndicator target={document.getElementById("root")}/>;
}

export default ScrollIndicator;
